import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Building2, FileStack, HardHat, Umbrella, FileSignature, Users, ChevronUp, ChevronDown } from "lucide-react";
import api from "@/lib/api";

// Icons per folder. Colors are pulled from the Files tab's own palette
// (soft neutral tile, single accent blue) so the strip feels native to
// the tab rather than a bright sticker sheet.
const STYLES = {
  "Corporate Documents": { Icon: Building2, short: "Corporate" },
  Submittals: { Icon: FileStack, short: "Submittals" },
  WSIB: { Icon: HardHat, short: "WSIB" },
  Insurance: { Icon: Umbrella, short: "Insurance" },
  "Stat Decs": { Icon: FileSignature, short: "Stat Decs" },
  "Employee Files": { Icon: Users, short: "Employees" },
};

const STORAGE_KEY = "sysFolderBarCollapsed";

const SystemFolderBar = ({ onOpen }) => {
  const [folders, setFolders] = useState([]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    let alive = true;
    api.get("/files/system")
      .then((r) => { if (alive) setFolders(r.data?.folders || []); })
      .catch(() => { if (alive) setFolders([]); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  if (folders.length === 0) return null;

  const openFolder = (f) => {
    if (onOpen) return onOpen(f);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", "files");
        p.set("folder", f.file_id);
        return p;
      },
      { replace: false }
    );
  };

  return (
    <div
      data-testid="system-folder-bar"
      className="relative shrink-0 border-b border-gray-200 bg-[#faf9f8] px-3 sm:px-6"
    >
      <div
        className={`grid grid-cols-3 gap-1.5 overflow-hidden transition-all duration-300 sm:grid-cols-6 sm:gap-4 ${
          collapsed ? "max-h-0 py-0 opacity-0" : "max-h-64 py-2.5 opacity-100 sm:py-3"
        }`}
      >
        {folders.map((f) => {
          const s = STYLES[f.name] || { Icon: FileStack, short: f.name };
          const { Icon } = s;
          return (
            <button
              key={f.file_id}
              type="button"
              onClick={() => openFolder(f)}
              data-testid={`sys-folder-${(f.name || "").toLowerCase().replace(/\s+/g, "-")}`}
              title={f.name}
              className="group relative flex min-w-0 flex-col items-center gap-1 rounded-lg px-1 py-0.5 outline-none focus-visible:ring-2 focus-visible:ring-[#0364b8]/60 sm:gap-1.5"
            >
              <span className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm transition-all duration-200 group-hover:-translate-y-0.5 group-hover:border-[#0364b8] group-hover:shadow-md group-active:scale-95 sm:h-14 sm:w-14 sm:rounded-xl">
                <Icon className="h-4 w-4 text-[#0364b8] transition-colors group-hover:text-[#0358a0] sm:h-6 sm:w-6" />
              </span>
              <span className="w-full truncate text-center font-mono text-[8.5px] uppercase tracking-[0.1em] text-gray-600 transition-colors group-hover:text-[#0364b8] sm:text-[10.5px] sm:tracking-[0.12em]">
                {s.short}
              </span>
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        data-testid="system-folder-bar-toggle"
        aria-label={collapsed ? "Expand shortcuts" : "Collapse shortcuts"}
        title={collapsed ? "Expand shortcuts" : "Collapse shortcuts"}
        className="absolute right-3 -bottom-2.5 z-10 flex h-5 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-[#0364b8] shadow-sm transition-colors hover:border-[#0364b8] hover:text-[#0358a0] sm:right-6"
      >
        {collapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
};

export default SystemFolderBar;
